import React, { useRef } from "react";
import YouTube, { type imgResolution } from "react-lite-youtube-embed";
import { pushToDataLayer } from "../../utils/google";
import * as styles from "./YouTubeLite.module.scss";

interface YouTubeLiteProps {
	url?: string;
	youtubeId?: string;
	title: string;
	poster?: imgResolution;
}

export const YouTubeLite: React.FC<YouTubeLiteProps> = ({
	url,
	youtubeId,
	title,
	poster,
}) => {
	const videoStartedRef = useRef(false);
	const playerRef = useRef<YT.Player>(null);
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const progressPushedRef = useRef<{ [key: number]: boolean }>({});
	const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

	let id = url || youtubeId || "";
	if (id?.includes("?v=")) {
		id = id.split("?v=")[1];
	}

	const loadYouTubeAPI = () => {
		if (iframeRef.current && window.YT) {
			loadYouTubePlayer(iframeRef.current);
		} else {
			const script = document.createElement("script");
			script.src = "https://www.youtube.com/iframe_api";
			script.onload = () => {
				loadYouTubePlayer(iframeRef.current);
			};
			document.body.appendChild(script);
		}
	};

	const loadYouTubePlayer = (iframe: HTMLIFrameElement) => {
		// @ts-ignore - 'ready' doesn't exist in our types package but does in the YouTube API
		window.YT.ready(() => {
			playerRef.current = new window.YT.Player(iframe, {
				events: {
					onStateChange: onPlayerStateChange,
				},
			});
		});
	};

	const onPlayerStateChange = (event: YT.OnStateChangeEvent) => {
		if (event.data === window.YT.PlayerState.PLAYING) {
			if (!progressIntervalRef.current) {
				startTrackingProgress(event);
			}

			// Only push the video_start event once
			if (!videoStartedRef.current) {
				videoStartedRef.current = true;
				pushToDataLayer({
					event: "video_start",
					videoId: id,
					// Use the YouTube API to get the video title as it doesn't always exist in the props
					// @ts-ignore - 'getVideoData' doesn't exist in our types package but does in the YouTube API
					videoTitle: event.target.getVideoData().title,
				});
			}
			return;
		}
		// Prevent progress tracking when video is paused
		if (event.data === window.YT.PlayerState.PAUSED) {
			stopTrackingProgress();
		}
		if (event.data === window.YT.PlayerState.ENDED) {
			pushToDataLayer({
				event: "video_complete",
				videoId: id,
				// Use the YouTube API to get the video title as it doesn't always exist in the props
				// @ts-ignore - 'getVideoData' doesn't exist in our types package but does in the YouTube API
				videoTitle: event.target.getVideoData().title,
			});
			stopTrackingProgress();
		}
	};

	const startTrackingProgress = (event: YT.OnStateChangeEvent) => {
		if (playerRef.current) {
			progressIntervalRef.current = setInterval(() => {
				const currentTime = playerRef.current.getCurrentTime();
				const videoDuration = playerRef.current.getDuration();
				const percentageWatched = (currentTime / videoDuration) * 100;

				const progressPoints = [10, 25, 50, 75];

				for (let i = 0; i < progressPoints.length; i++) {
					const progressPoint = progressPoints[i];
					const nextProgressPoint = progressPoints[i + 1] || 100;

					if (
						percentageWatched >= progressPoint &&
						percentageWatched < nextProgressPoint &&
						!progressPushedRef.current[progressPoint]
					) {
						pushToDataLayer({
							event: "video_progress",
							videoId: id,
							// Use the YouTube API to get the video title as it doesn't always exist in the props
							// @ts-ignore - 'getVideoData' doesn't exist in our types package but does in the YouTube API
							videoTitle: event.target.getVideoData().title,
							videoProgress: progressPoint.toString(),
						});
						progressPushedRef.current[progressPoint] = true;
					}
				}
			}, 1000);
		}
	};
	const stopTrackingProgress = () => {
		if (progressIntervalRef.current) {
			clearInterval(progressIntervalRef.current);
			progressIntervalRef.current = null;
		}
	};

	return (
		<YouTube
			title={title}
			wrapperClass={styles.youtubewrapper}
			iframeClass={styles.youtubeiframe}
			playerClass={styles.youtubeplayer}
			activatedClass={styles.youtubeactivated}
			poster={poster}
			id={id}
			params="autoplay=0&enablejsapi=1"
			onIframeAdded={() => {
				if (iframeRef.current && typeof window !== "undefined") {
					loadYouTubeAPI();
				}
			}}
			ref={iframeRef}
		/>
	);
};

export default YouTubeLite;
